import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const FileConfirmModal = ({ isOpen, onClose, file, onConfirm, onCancel }) => {
  const { t } = useTranslation('chat');
  if (!isOpen) return null;

  const handleConfirm = () => { if (file) onConfirm?.(file); onClose?.(); };
  const handleCancel = () => { onCancel?.(); onClose?.(); };

  const fileSizeMB = file ? (file.size / (1024 * 1024)).toFixed(2) : '0.00';
  const fileName = file?.name ?? '';

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="fixed inset-0 z-[100001] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={(e) => { if (e.target === e.currentTarget) handleCancel(); }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 8 }}
          transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-sm bg-[var(--color-bg-base)] rounded-2xl border border-white/[0.08] shadow-[0_32px_80px_rgba(0,0,0,0.7)] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#2563eb]/10 border border-[#3d8ef0]/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-[#3d8ef0]" strokeWidth={2} />
              </div>
              <h3 className="text-base font-semibold text-[var(--color-text-primary)]">{t('fileConfirmModal.title')}</h3>
            </div>
            <button
              onClick={handleCancel}
              className="p-1.5 rounded-lg text-[#4a6380] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.06] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            <div className="flex items-center gap-3 p-3.5 rounded-xl bg-[var(--color-bg-muted)] border border-white/[0.06] mb-6">
              <div className="w-9 h-9 rounded-lg bg-[#2563eb]/10 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4.5 h-4.5 text-[#3d8ef0]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">{fileName || '—'}</p>
                <p className="text-xs text-[var(--color-text-muted)]">{fileSizeMB} MB</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-xl text-sm font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-white/[0.05] transition-colors"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={handleConfirm}
                disabled={!file}
                className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#2563eb] hover:bg-[#3d8ef0] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                <Send className="w-3.5 h-3.5" />
                {t('fileConfirmModal.send')}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default FileConfirmModal;
